<script setup lang="ts">
import { router, usePage } from '@inertiajs/vue3'
import { computed, onMounted, onUnmounted, ref, watchEffect } from 'vue'
import { PageProps } from '@/Models/Inertia'
import { route } from 'ziggy-js'
import axios from 'axios'
import VPill from '@/Components/BaseComponents/VPill.vue'

const page = usePage<PageProps>()
const appDetails = page.props.app_details

onMounted(() => {
    checkAuth()
})

function checkAuth() {
    axios
        .get(route('is_authenticated'))
        .then((res) => {
            if (res.data.authenticated) {
                router.visit('/')
            }
        })
        .catch(() => {
            // Niet-kritiek: gebruiker blijft gewoon op de login pagina
        })
}

// #6250: enkel de talen van de whitelabel; bij één taal (Frankrijk) geen switcher.
const localeLocked = computed(() => page.props.app_details?.locale_locked === true)
const languages = computed(() =>
    ['nl', 'en', 'fr'].filter((lang) =>
        (page.props.app_details?.available_languages ?? ['nl', 'en', 'fr']).includes(lang)
    )
)
const currentLocale = computed(() => page.props.locale)

const backgroundImageDiv = ref<HTMLElement | null>(null)

watchEffect(() => {
    if (backgroundImageDiv.value) {
        updateBackgroundImage()
    }
})

const updateBackgroundImage = () => {
    if (appDetails['theme'] === 'liantis') {
        // Set background color to primary color for Liantis theme
        const primaryColor = appDetails['primary_color'] || '#7843a5'
        backgroundImageDiv.value.style.backgroundColor = primaryColor
        backgroundImageDiv.value.style.backgroundImage = 'none'
        return
    }
    let gradient = `linear-gradient(90deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.4) 100%)`

    const customAuthColorsGradient = appDetails['custom_auth_colors']?.gradient

    if (customAuthColorsGradient) {
        const primaryColor = appDetails['custom_auth_colors']?.primary_color

        if (primaryColor) {
            gradient = `linear-gradient(90deg, ${primaryColor}99 0%, ${primaryColor}99 100%)`
        }
    }

    let imageNumber = 1

    if (appDetails['theme'] === 'sage') {
        imageNumber = Math.floor(Math.random() * 3) + 1
    }

    const imageUrl = `url('/img/${appDetails['theme']}/backgrounds/image-${imageNumber}.jpg')`

    backgroundImageDiv.value.style.backgroundImage = `${gradient}, ${imageUrl}`
}

const isLiantis = computed(() => {
    return appDetails['theme'] === 'liantis'
})

// De Liantis-korting stopt na 31/12/2026: de backend levert dan 0 en de kortingszinnen verdwijnen.
const discountPercentage = computed(() => page.props.config?.numnum?.price_discount_percentage ?? 0)

const backgroundClasses = computed(() => {
    return 'bg-no-repeat bg-cover bg-[position:50%_50%] h-screen w-screen block md:grid md:grid-cols-2'
})

const setLanguage = (lang: string) => {
    if (lang === currentLocale.value) return

    const currentPath = window.location.pathname
    const pathWithoutLocale = currentPath.replace(/^\/[a-zA-Z]{2}(\/|$)/, '/')
    const newUrl = `/${lang}${pathWithoutLocale}`

    router.visit(newUrl, {
        preserveState: true,
        preserveScroll: true,
    })
}
</script>
<template>
    <!-- Subtiele env badge linksboven op auth pages, alleen non-production -->
    <VPill
        v-if="page.props.app_env && page.props.app_env !== 'production'"
        color="orange"
        variant="solid"
        class="fixed top-3 left-3 z-50 uppercase tracking-wider shadow"
    >
        {{ page.props.app_env }}
    </VPill>
    <div :class="backgroundClasses" ref="backgroundImageDiv">
        <section v-if="isLiantis" class="h-screen flex flex-col justify-center text-white p-16">
            <div class="mb-12">
                <img
                    :src="'/img/' + appDetails['theme'] + '/logos/logo-white.svg'"
                    :alt="appDetails['theme']"
                    class="max-w-60"
                />
            </div>

            <div class="text-white md:text-2xl font-bold mb-8">
                {{
                    discountPercentage > 0
                        ? $t('liantis.x_pct_client_discount', {
                              percentage: String(discountPercentage),
                          })
                        : $t('liantis.liantis_login_title')
                }}
            </div>

            <div class="text-white space-y-6 text-lg opacity-85">
                <p class="text-white md:text-lg">
                    {{
                        discountPercentage > 0
                            ? $t('liantis.liantis_login_baseline_1', {
                                  percentage: String(discountPercentage),
                              })
                            : $t('liantis.liantis_login_baseline_1_no_discount')
                    }}
                </p>
                <p class="text-white md:text-lg">
                    {{ $t('liantis.liantis_login_baseline_2') }}
                </p>
                <p class="text-white md:text-lg">
                    {{
                        discountPercentage > 0
                            ? $t('liantis.liantis_login_baseline_3', {
                                  percentage: String(discountPercentage),
                              })
                            : $t('liantis.liantis_login_baseline_3_no_discount')
                    }}
                </p>
            </div>
        </section>
        <section
            class="md:col-start-2 flex flex-col items-center justify-center h-screen bg-white lg:pl-16 lg:pr-4"
        >
            <div class="max-w-[200px]">
                <img
                    :src="
                        '/img/' +
                        appDetails['theme'] +
                        '/logos/' +
                        (appDetails['login_logo'] || appDetails['logo'])
                    "
                    :alt="appDetails['theme']"
                    class="max-w-40 mx-auto mb-16"
                />
            </div>

            <slot name="header" />

            <div class="px-2 lg:px-0 xl:px-14 w-full">
                <slot name="form" />
            </div>
        </section>

        <!--        Language switcher-->
        <div v-if="!localeLocked" class="absolute right-4 top-4">
            <template v-for="(lang, idx) of languages">
                <span
                    class="cursor-pointer text-sm"
                    @click="setLanguage(lang)"
                    :class="currentLocale === lang ? 'font-bold' : 'text-gray-500'"
                >
                    {{ lang }}
                </span>
                <span v-if="idx < languages.length - 1" class="px-1 cursor-default"
                    >&nbsp;&middot;&nbsp;</span
                >
            </template>
        </div>
    </div>
</template>

<style lang="scss" scoped></style>
